class ProductCarousel extends HTMLElement {
  constructor() {
    super();
    this.marqueeRunning = false;
    this.marqueePaused = false;
    this.marqueeOffset = 0;
    this.marqueeLoopWidth = 0;
    this.lastFrameTime = 0;
    this.rafId = null;
    this.hasAnimated = false;
    this.marqueeStarted = false;
    this.nudgeDone = false;
    this.reducedMotion = false;
    this.isFinePointer = false;
    this.isTouchRail = false;
    this.focusInsideRail = false;
    this.pointerInsideRail = false;

    this.onResize = this.onResize.bind(this);
    this.marqueeTick = this.marqueeTick.bind(this);
    this.onRailPointerEnter = this.onRailPointerEnter.bind(this);
    this.onRailPointerLeave = this.onRailPointerLeave.bind(this);
    this.onRailFocusIn = this.onRailFocusIn.bind(this);
    this.onRailFocusOut = this.onRailFocusOut.bind(this);
  }

  connectedCallback() {
    this.railStage = this.querySelector('[data-rail-stage]');
    this.railTrack = this.querySelector('[data-rail-track]');
    this.railViewport = this.querySelector('[data-rail-viewport]');
    this.cloneTrack = this.querySelector('[data-rail-clone]');

    if (!this.railStage || !this.railTrack || !this.railViewport) return;

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.canDrift = !this.reducedMotion && typeof window.AlsDriftRail === 'function';
    this.pointerQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
    this.isFinePointer = this.pointerQuery.matches;
    this.updateRailMode();

    this.baseSpeed = parseFloat(this.dataset.speedPx) || 50;
    this.pauseOnHover = this.dataset.pauseOnHover !== 'false';

    this.railViewport.addEventListener('pointerenter', this.onRailPointerEnter);
    this.railViewport.addEventListener('pointerleave', this.onRailPointerLeave);
    this.railViewport.addEventListener('focusin', this.onRailFocusIn);
    this.railViewport.addEventListener('focusout', this.onRailFocusOut);

    this.pointerQuery.addEventListener('change', this.onResize);
    window.addEventListener('resize', this.onResize);

    this.initObserver();
  }

  disconnectedCallback() {
    this.stopMarquee();
    this.observer?.disconnect();
    this.entranceTimer?.forEach((id) => window.clearTimeout(id));
    this.pointerQuery?.removeEventListener('change', this.onResize);
    window.removeEventListener('resize', this.onResize);
    this.railViewport?.removeEventListener('pointerenter', this.onRailPointerEnter);
    this.railViewport?.removeEventListener('pointerleave', this.onRailPointerLeave);
    this.railViewport?.removeEventListener('focusin', this.onRailFocusIn);
    this.railViewport?.removeEventListener('focusout', this.onRailFocusOut);
  }

  updateRailMode() {
    this.isFinePointer = this.pointerQuery.matches;
    const isDrift = !this.isFinePointer && this.canDrift;
    const mode = isDrift ? 'drift' : !this.isFinePointer || this.reducedMotion ? 'touch' : 'marquee';
    if (mode === this.railMode) return;
    this.railMode = mode;
    this.isDrift = isDrift;
    this.isTouchRail = mode === 'touch';
    this.classList.toggle('is-touch-rail', this.isTouchRail);
    this.classList.toggle('is-drift-rail', this.isDrift);

    if (this.isTouchRail) {
      this.stopMarquee();
      if (this.railStage) this.railStage.style.transform = '';
    } else if (this.marqueeStarted) {
      this.setupMarquee();
    }
  }

  onResize() {
    this.updateRailMode();
    if (this.railMode === 'marquee' && this.marqueeRunning) {
      this.measureMarquee();
    }
  }

  initObserver() {
    if (this.reducedMotion) {
      this.classList.add('is-visible', 'is-meta-visible', 'is-rail-visible', 'is-products-visible');
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.hasAnimated) {
            this.hasAnimated = true;
            this.runEntranceSequence();
            this.observer.disconnect();
          }
        });
      },
      { threshold: 0.18, rootMargin: '0px 0px -6% 0px' }
    );

    this.observer.observe(this);
  }

  runEntranceSequence() {
    this.entranceTimer = [];

    this.entranceTimer.push(window.setTimeout(() => this.classList.add('is-visible'), 0));
    this.entranceTimer.push(window.setTimeout(() => this.classList.add('is-meta-visible'), 180));
    this.entranceTimer.push(window.setTimeout(() => this.classList.add('is-rail-visible'), 340));
    this.entranceTimer.push(
      window.setTimeout(() => {
        this.classList.add('is-products-visible');
        if (this.isTouchRail) {
          this.runMobileNudge();
        } else {
          this.entranceTimer.push(
            window.setTimeout(() => {
              this.marqueeStarted = true;
              this.setupMarquee();
            }, 580)
          );
        }
      }, 460)
    );
  }

  runMobileNudge() {
    if (this.reducedMotion || !this.isTouchRail || this.nudgeDone || !this.railViewport) return;

    this.nudgeDone = true;
    const viewport = this.railViewport;
    const distance = Math.min(18, Math.max(14, viewport.clientWidth * 0.04));

    window.setTimeout(() => {
      viewport.scrollTo({ left: distance, behavior: 'smooth' });
      window.setTimeout(() => {
        viewport.scrollTo({ left: 0, behavior: 'smooth' });
      }, 720);
    }, 120);
  }

  measureMarquee() {
    if (!this.railTrack) return;

    const primaryWidth = this.railTrack.scrollWidth;
    this.marqueeLoopWidth = primaryWidth;

    if (this.cloneTrack) {
      this.cloneTrack.style.width = `${primaryWidth}px`;
    }
  }

  setupMarquee() {
    if (this.isTouchRail || this.reducedMotion) return;

    this.stopMarquee();

    if (this.isDrift) {
      this.prepareDriftClones();
      if (!this.drift) {
        this.drift = new window.AlsDriftRail({
          viewport: this.railViewport,
          stage: this.railStage,
          speed: 50,
          getItems: () => this.railStage.querySelectorAll('.pc__product'),
          getLoopWidth: () => (this.cloneTrack ? this.cloneTrack.offsetLeft - this.railTrack.offsetLeft : 0),
        });
      }
      this.marqueeRunning = true;
      this.drift.start({ reset: !this.driftStarted });
      this.driftStarted = true;
      return;
    }

    this.measureMarquee();

    if (this.marqueeLoopWidth <= 0) return;

    this.marqueeRunning = true;
    this.marqueePaused = this.shouldPause();
    this.lastFrameTime = performance.now();
    this.rafId = requestAnimationFrame(this.marqueeTick);
  }

  shouldPause() {
    if (!this.pauseOnHover) return false;
    return this.pointerInsideRail || this.focusInsideRail;
  }

  marqueeTick(now) {
    if (!this.marqueeRunning) return;

    const delta = Math.min(now - this.lastFrameTime, 48);
    this.lastFrameTime = now;

    if (!this.marqueePaused && this.marqueeLoopWidth > 0) {
      this.marqueeOffset -= (this.baseSpeed * delta) / 1000;

      while (this.marqueeOffset <= -this.marqueeLoopWidth) {
        this.marqueeOffset += this.marqueeLoopWidth;
      }

      this.railStage.style.transform = `translate3d(${this.marqueeOffset}px, 0, 0)`;
    }

    this.rafId = requestAnimationFrame(this.marqueeTick);
  }

  // The server-rendered clones are inert placeholders; on touch they are on
  // screen half the time, so swap them for real (tabindex -1) product links.
  prepareDriftClones() {
    if (!this.cloneTrack || this.cloneTrack.dataset.alsLinked) return;
    this.cloneTrack.dataset.alsLinked = 'true';
    this.cloneTrack.style.width = '';
    const fragment = document.createDocumentFragment();
    Array.from(this.railTrack.children).forEach((item) => {
      const clone = item.cloneNode(true);
      clone.classList.add('pc__product--clone');
      clone.setAttribute('tabindex', '-1');
      clone.setAttribute('aria-hidden', 'true');
      fragment.appendChild(clone);
    });
    this.cloneTrack.replaceChildren(fragment);
  }

  stopMarquee() {
    this.marqueeRunning = false;
    this.drift?.stop();
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  pauseMarquee() {
    this.marqueePaused = true;
  }

  resumeMarquee() {
    if (!this.isTouchRail && !this.reducedMotion && this.marqueeStarted) {
      this.marqueePaused = this.shouldPause();
      if (!this.marqueeRunning) {
        this.setupMarquee();
      }
    }
  }

  onRailPointerEnter() {
    this.pointerInsideRail = true;
    this.pauseMarquee();
  }

  onRailPointerLeave() {
    this.pointerInsideRail = false;
    this.resumeMarquee();
  }

  onRailFocusIn() {
    this.focusInsideRail = true;
    this.pauseMarquee();
  }

  onRailFocusOut(event) {
    if (this.railViewport?.contains(event.relatedTarget)) return;
    this.focusInsideRail = false;
    this.resumeMarquee();
  }
}

if (!customElements.get('product-carousel')) {
  customElements.define('product-carousel', ProductCarousel);
}
